import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { resolveTaskTicketId } from '../../utils/taskUtils';

/**
 * TicketIdDisplay
 * 
 * A reusable component to display task ticket IDs with support for multi-task groups.
 * Shows a limited number of IDs and provides an overflow badge with a tooltip for the rest.
 * 
 * @param {Object}  task       - The task object (can be a summary or detail)
 * @param {number}  maxVisible - Number of IDs to show before collapsing into a badge
 * @param {boolean} clickable  - Whether clicking an ID should navigate to the task detail page
 * @param {string}  fontSize   - Optional font size override
 */
const TicketIdDisplay = ({ 
    task, 
    maxVisible = 1, 
    clickable = true,
    fontSize = "12px"
}) => {
    const ticketData = useMemo(() => {
        if (!task) return [];
        
        // 1. Identify potential sources of ticket IDs
        let items = [];
        if (Array.isArray(task.tasks) && task.tasks.length > 0) {
            items = task.tasks;
        } else if (Array.isArray(task.assignees) && task.assignees.length > 0) {
            items = task.assignees;
        } else if (Array.isArray(task.assignee) && task.assignee.length > 0) {
            // Some API shapes might have a singular 'assignee' as an array
            items = task.assignee;
        }

        let results = [];
        if (items.length > 0) {
            results = items.map(item => ({
                ticketId: resolveTaskTicketId(item),
                actualId: item.id || item.taskId || item.TaskId || item.userId
            })).filter(td => td.ticketId);

            // Fallback: If mapping over assignees yielded no valid ticket IDs, fallback to the parent task's ticket
            if (results.length === 0) {
                const ticket = resolveTaskTicketId(task);
                if (ticket) {
                    results = [{ 
                        ticketId: ticket, 
                        actualId: task.id || task.taskId || task.userId 
                    }];
                }
            }
        } else {
            const ticket = resolveTaskTicketId(task);
            if (ticket) {
                results = [{ 
                    ticketId: ticket, 
                    actualId: task.id || task.taskId || task.userId 
                }];
            }
        }

        // 2. Deduplicate by Ticket ID
        const uniqueResults = [];
        const seen = new Set();
        results.forEach(td => {
            if (td.ticketId && !seen.has(td.ticketId)) {
                uniqueResults.push(td);
                seen.add(td.ticketId);
            }
        });
        
        return uniqueResults;
    }, [task]);

    if (ticketData.length === 0) {
        return <span style={{ color: "#94a3b8" }}>—</span>;
    }

    const visibleTickets = ticketData.slice(0, maxVisible);
    const overflowTickets = ticketData.slice(maxVisible);

    const handleIdClick = (e, id) => {
        if (!clickable || !id) return;
        e?.stopPropagation();
        window.open(`/task/${id}`, "_blank");
    };

    return (
        <div 
            className="ticket-id-display-container"
            style={{ display: "flex", flexWrap: "wrap", gap: "2px 4px", alignItems: "center" }}
        >
            {visibleTickets.map((td, idx) => (
                <React.Fragment key={td.ticketId || idx}>
                    <span
                        onClick={(e) => handleIdClick(e, td.actualId)}
                        style={{ 
                            color: clickable ? "#2563EB" : "inherit", 
                            fontWeight: 600, 
                            fontSize: fontSize, 
                            cursor: (clickable && td.actualId) ? "pointer" : "default",
                            transition: "color 0.15s, text-decoration 0.15s"
                        }}
                        onMouseEnter={(e) => {
                            if (clickable && td.actualId) e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            if (clickable && td.actualId) e.currentTarget.style.textDecoration = 'none';
                        }}
                    >
                        {td.ticketId}
                    </span>
                    {idx < visibleTickets.length - 1 && (
                        <span style={{ color: '#cbd5e1', fontSize: '10px' }}>,</span>
                    )}
                </React.Fragment>
            ))}

            {overflowTickets.length > 0 && (
                <Tooltip
                    title={
                        <Box sx={{ py: 0.5, px: 0.5 }}>
                            <Box sx={{ fontWeight: 600, fontSize: 11, mb: 0.5, opacity: 0.75, letterSpacing: "0.03em", color: "#fff" }}>
                                All Ticket IDs
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {ticketData.map((td) => (
                                    <Box
                                        key={td.ticketId}
                                        onClick={(e) => handleIdClick(e, td.actualId)}
                                        sx={{
                                            fontSize: 12,
                                            lineHeight: 1.8,
                                            color: "#fff",
                                            cursor: (clickable && td.actualId) ? "pointer" : "default",
                                            "&:hover": (clickable && td.actualId) ? { textDecoration: "underline", color: "#60a5fa" } : {}
                                        }}
                                    >
                                        {td.ticketId}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    }
                    interactive
                    arrow
                    placement="top"
                    enterDelay={150}
                >
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#2563EB",
                            bgcolor: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            borderRadius: "10px",
                            px: "6px",
                            py: "1px",
                            cursor: "default",
                            lineHeight: 1.6,
                            userSelect: "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        +{overflowTickets.length}
                    </Box>
                </Tooltip>
            )}
        </div>
    );
};

export default TicketIdDisplay;
